import FlightLanding from '@ixigo/icons/FlightLanding';
import FlightTakeoff from '@ixigo/icons/FlightTakeoff';
import type { ComponentType, SVGProps } from 'react';

/**
 * Trip type / direction selected by the user (FLIG-22203). Matches the backend's
 * `tripType` field (autocomplete request + `cabs/api/v1/trip-type` response):
 * - `AIRPORT_DROP_OFF` → "To Airport" (airport is the destination)
 * - `AIRPORT_PICK_UP`  → "From Airport" (airport is the origin)
 *
 * Name collides (not in meaning) with the analytics tracker's existing
 * `tripType: 'airport' | 'outstation' | 'rental'` field — that's the product
 * category, output as the `Type` event attribute. This `TripType` is the
 * airport direction. The tracker is unchanged on this branch.
 */
export const TRIP_TYPE = {
	AIRPORT_DROP_OFF: 'AIRPORT_DROP_OFF',
	AIRPORT_PICK_UP: 'AIRPORT_PICK_UP',
} as const;

export type TripType = (typeof TRIP_TYPE)[keyof typeof TRIP_TYPE];

/** The two location fields a trip is composed of. */
type LocationField = 'pickup' | 'drop';

interface TripTypeConfig {
	/** Segmented-control label */
	label: string;
	/** The field that must hold a DB airport for this trip type */
	airportField: LocationField;
	/**
	 * Flight icon for the airport side: "To Airport" (drop-off) = departing →
	 * takeoff, "From Airport" (pickup) = arriving → landing.
	 */
	flightIcon: ComponentType<SVGProps<SVGSVGElement>>;
	/** Per-field placeholders; the airport side reads "Airport Location". */
	placeholders: Record<LocationField, string>;
}

/**
 * Single source of truth for everything that varies by trip type — label,
 * airport side, flight icon, and field placeholders. The helpers below are thin
 * readers over this map; add a trip type by adding one entry.
 */
export const TRIP_TYPE_CONFIG: Record<TripType, TripTypeConfig> = {
	[TRIP_TYPE.AIRPORT_DROP_OFF]: {
		label: 'To Airport',
		airportField: 'drop',
		flightIcon: FlightTakeoff,
		placeholders: { pickup: 'Pickup Location', drop: 'Airport Location' },
	},
	[TRIP_TYPE.AIRPORT_PICK_UP]: {
		label: 'From Airport',
		airportField: 'pickup',
		flightIcon: FlightLanding,
		placeholders: { pickup: 'Airport Location', drop: 'Drop-off Location' },
	},
};

/** Narrow an unknown (e.g. a URL param) to a valid TripType */
export const isTripType = (value: unknown): value is TripType =>
	value === TRIP_TYPE.AIRPORT_DROP_OFF || value === TRIP_TYPE.AIRPORT_PICK_UP;

/**
 * Infer the trip type from which side is a backend-tagged airport: airport on
 * the pickup side → "From Airport", airport on the drop side → "To Airport".
 * Returns `undefined` when neither (or both) side is an airport, so the caller
 * can leave the current type untouched. Both args take the location's `type`
 * tag (e.g. a LocationSuggestion's `type`).
 */
export const inferTripTypeFromAirportSide = (
	pickupType?: string,
	dropType?: string,
): TripType | undefined => {
	if (pickupType === 'airport' && dropType !== 'airport') {
		return TRIP_TYPE.AIRPORT_PICK_UP;
	}
	if (dropType === 'airport' && pickupType !== 'airport') {
		return TRIP_TYPE.AIRPORT_DROP_OFF;
	}
	return undefined;
};
